import { useEffect, useCallback, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import {
    socketService,
    VerificationStatusUpdate,
    UploadProgressUpdate
} from '@/services/socketService';
import { useSocket } from './useSocket';

export interface VerificationSocketState {
    isSubscribed: boolean;
    lastStatusUpdate?: VerificationStatusUpdate;
    uploadProgress: Record<string, number>; // documentId -> progress
    errors: Array<{ message: string; timestamp: Date; code?: string }>;
}

// Global subscription tracking to prevent multiple subscriptions
let globalSubscriptionActive = false;
let subscriptionInstanceCount = 0;

// Global handler registry to persist across all hook instances
class GlobalVerificationHandlerRegistry {
    private static instance: GlobalVerificationHandlerRegistry;
    private handlers: Map<string, (data: VerificationStatusUpdate) => void> = new Map();

    static getInstance(): GlobalVerificationHandlerRegistry {
        if (!GlobalVerificationHandlerRegistry.instance) {
            GlobalVerificationHandlerRegistry.instance = new GlobalVerificationHandlerRegistry();
        }
        return GlobalVerificationHandlerRegistry.instance;
    }

    addHandler(handlerId: string, handler: (data: VerificationStatusUpdate) => void): () => void {
        console.log('🔗 GLOBAL REGISTRY - Adding handler:', handlerId);
        console.log('🔗 GLOBAL REGISTRY - Handler count before adding:', this.handlers.size);

        this.handlers.set(handlerId, handler);

        console.log('🔗 GLOBAL REGISTRY - Handler count after adding:', this.handlers.size);
        console.log('🔗 GLOBAL REGISTRY - Active handler IDs:', Array.from(this.handlers.keys()));

        // Return cleanup function
        return () => {
            console.log('🔓 GLOBAL REGISTRY - Removing handler:', handlerId);
            console.log('🔓 GLOBAL REGISTRY - Handler count before removing:', this.handlers.size);

            this.handlers.delete(handlerId);

            console.log('🔓 GLOBAL REGISTRY - Handler count after removing:', this.handlers.size);
            console.log('🔓 GLOBAL REGISTRY - Remaining handler IDs:', Array.from(this.handlers.keys()));
        };
    }

    callAllHandlers(data: VerificationStatusUpdate): void {
        console.log('🔄 GLOBAL REGISTRY - Calling', this.handlers.size, 'handlers');
        console.log('🔄 GLOBAL REGISTRY - Active handler IDs:', Array.from(this.handlers.keys()));

        if (this.handlers.size === 0) {
            console.warn('⚠️ GLOBAL REGISTRY - NO HANDLERS REGISTERED - This means UI won\'t update!');
        }

        this.handlers.forEach((handler, handlerId) => {
            try {
                console.log(`🔄 GLOBAL REGISTRY - Calling handler ${handlerId}`);
                handler(data);
                console.log(`✅ GLOBAL REGISTRY - Handler ${handlerId} executed successfully`);
            } catch (error) {
                console.error(`❌ GLOBAL REGISTRY - Error calling handler ${handlerId}:`, error);
            }
        });
    }

    getHandlerCount(): number {
        return this.handlers.size;
    }
}

const globalHandlerRegistry = GlobalVerificationHandlerRegistry.getInstance();

export const useVerificationSocket = () => {
    const { user } = useSelector((state: RootState) => state.auth);
    const { connectionState } = useSocket();

    const [verificationState, setVerificationState] = useState<VerificationSocketState>({
        isSubscribed: false,
        uploadProgress: {},
        errors: []
    });

    const instanceIdRef = useRef<number>(0);

    // Subscribe to verification updates when connected and user is available
    useEffect(() => {
        if (!connectionState.isConnected || !user) {
            if (verificationState.isSubscribed) {
                setVerificationState(prev => ({
                    ...prev,
                    isSubscribed: false
                }));
            }
            return;
        }

        // Increment subscription instance counter
        subscriptionInstanceCount++;
        instanceIdRef.current = subscriptionInstanceCount;

        console.log(`Verification socket instance ${instanceIdRef.current} checking subscription for user:`, user._id);

        // Only create subscription if none exists globally
        if (!globalSubscriptionActive) {
            console.log(`Verification socket instance ${instanceIdRef.current} creating subscription for user:`, user._id);
            globalSubscriptionActive = true;
            socketService.subscribeToVerificationUpdates(user._id);
        } else {
            console.log(`Verification socket instance ${instanceIdRef.current} using existing subscription`);
        }

        setVerificationState(prev => ({
            ...prev,
            isSubscribed: true
        }));

        return () => {
            subscriptionInstanceCount--;
            console.log(`Verification socket instance ${instanceIdRef.current} unmounting, remaining: ${subscriptionInstanceCount}`);

            // Only unsubscribe when last instance unmounts
            if (subscriptionInstanceCount <= 0) {
                console.log('Last verification socket instance unmounting, unsubscribing');
                globalSubscriptionActive = false;
                socketService.unsubscribeFromVerificationUpdates();
                subscriptionInstanceCount = 0; // Reset to prevent negative values
            }
        };
    }, [connectionState.isConnected, user]);

    // Note: Using global handler registry instead of local refs

    // Set up event handlers only once
    useEffect(() => {
        const handleStatusUpdate = (data: VerificationStatusUpdate) => {
            console.log('🔔 Verification status update received in hook:', data);

            setVerificationState(prev => ({
                ...prev,
                lastStatusUpdate: data
            }));

            // Use global handler registry to call all handlers
            globalHandlerRegistry.callAllHandlers(data);
        };

        const handleUploadProgress = (data: UploadProgressUpdate) => {
            console.log('Upload progress update received:', data);

            setVerificationState(prev => ({
                ...prev,
                uploadProgress: {
                    ...prev.uploadProgress,
                    [data.documentId]: data.progress
                }
            }));

            // Remove from progress tracking when upload is complete or failed
            if (data.stage === 'complete' || data.stage === 'error') {
                setTimeout(() => {
                    setVerificationState(currentPrev => ({
                        ...currentPrev,
                        uploadProgress: Object.fromEntries(
                            Object.entries(currentPrev.uploadProgress).filter(([documentId]) => documentId !== data.documentId)
                        )
                    }));
                }, 3000);
            }
        };

        const handleVerificationError = (data: { message: string; code?: string }) => {
            console.error('Verification error received:', data);

            setVerificationState(prev => ({
                ...prev,
                errors: [
                    ...prev.errors,
                    {
                        message: data.message,
                        code: data.code,
                        timestamp: new Date()
                    }
                ].slice(-10) // Keep only last 10 errors
            }));
        };

        // Register event handlers only once
        socketService.onVerificationStatusChanged(handleStatusUpdate);
        socketService.onUploadProgress(handleUploadProgress);
        socketService.onVerificationError(handleVerificationError);

        console.log('🔧 Verification socket handlers registered');

        // Cleanup handlers on unmount
        return () => {
            console.log('🧹 Cleaning up verification socket handlers');
            socketService.offVerificationStatusChanged();
            socketService.offUploadProgress();
            socketService.offVerificationError();
        };
    }, []); // Empty dependency array to prevent re-registration

    // Utility functions
    const emitUploadProgress = useCallback((documentId: string, progress: number) => {
        if (connectionState.isConnected) {
            socketService.emitUploadProgress(documentId, progress);
        }
    }, [connectionState.isConnected]);

    const clearErrors = useCallback(() => {
        setVerificationState(prev => ({
            ...prev,
            errors: []
        }));
    }, []);

    const clearUploadProgress = useCallback((documentId: string) => {
        setVerificationState(prev => ({
            ...prev,
            uploadProgress: Object.fromEntries(
                Object.entries(prev.uploadProgress).filter(([currentDocumentId]) => currentDocumentId !== documentId)
            )
        }));
    }, []);

    const getUploadProgress = useCallback((documentId: string): number | undefined => {
        return verificationState.uploadProgress[documentId];
    }, [verificationState.uploadProgress]);

    const hasActiveUploads = useCallback((): boolean => {
        return Object.values(verificationState.uploadProgress).some(
            progress => progress !== undefined && progress < 100
        );
    }, [verificationState.uploadProgress]);

    const getActiveUploadsCount = useCallback((): number => {
        return Object.values(verificationState.uploadProgress).filter(
            progress => progress !== undefined && progress < 100
        ).length;
    }, [verificationState.uploadProgress]);

    const subscribeToStatusUpdates = useCallback((handler: (data: VerificationStatusUpdate) => void) => {
        console.log('🔗 Subscribing to verification status updates via global registry');

        // Generate unique ID for this handler with timestamp to ensure uniqueness
        const handlerId = `${Date.now()}_${Math.random()}`;

        // Register handler with global registry
        const cleanupHandler = globalHandlerRegistry.addHandler(handlerId, handler);

        // Return cleanup function that removes handler from global registry
        return cleanupHandler;
    }, []);

    return {
        verificationState,
        emitUploadProgress,
        clearErrors,
        clearUploadProgress,
        getUploadProgress,
        hasActiveUploads,
        getActiveUploadsCount,
        subscribeToStatusUpdates,
        isConnected: connectionState.isConnected,
        connectionStatus: connectionState.connectionStatus
    };
};